<script lang="ts">
  import type { LocalInputFile } from '../../core/files/file.types';
  import { readLocalFile } from '../../core/files/fileKind';

  export let onFilesSelected: (files: LocalInputFile[]) => void = () => {};
  export let disabled = false;
  export let accept = '.afd,.aej,.txt,.csv';

  let isDragOver = false;
  let errorMessage: string | null = null;

  async function handleFiles(fileList: FileList | null): Promise<void> {
    if (!fileList || fileList.length === 0) return;
    errorMessage = null;
    const results: LocalInputFile[] = [];
    for (const file of Array.from(fileList)) {
      try {
        const localFile = await readLocalFile(file);
        results.push(localFile);
      } catch (err) {
        errorMessage = `Erro ao ler ${file.name}: ${err instanceof Error ? err.message : 'desconhecido'}`;
      }
    }
    if (results.length > 0) onFilesSelected(results);
  }

  function handleDrop(e: DragEvent): void {
    isDragOver = false;
    if (disabled) return;
    void handleFiles(e.dataTransfer?.files ?? null);
  }

  function handleChange(e: Event): void {
    const input = e.target as HTMLInputElement;
    void handleFiles(input.files);
  }
</script>

<div
  class="dropzone"
  class:drag-over={isDragOver}
  class:disabled
  role="region"
  aria-label="Área de envio de arquivos"
  on:dragover|preventDefault={() => { if (!disabled) isDragOver = true; }}
  on:dragleave={() => { isDragOver = false; }}
  on:drop|preventDefault={handleDrop}
>
  {#if disabled}
    <p class="disabled-message">Aceite o aviso de privacidade para continuar</p>
  {:else}
    <label class="dropzone-label">
      <span class="dropzone-text">
        Arraste arquivos AFD, AEJ ou CSV aqui, ou
        <span class="browse-link">clique para selecionar</span>
      </span>
      <input
        type="file"
        {accept}
        multiple
        class="file-input"
        on:change={handleChange}
        aria-label="Selecionar arquivos"
      />
    </label>
    {#if errorMessage}
      <p class="error-message" role="alert">{errorMessage}</p>
    {/if}
  {/if}
</div>

<style>
  .dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border: 2px dashed var(--ptt-gray-20, #ccc);
    border-radius: 8px;
    padding: 2rem;
    min-height: 120px;
    cursor: pointer;
    transition: border-color 0.2s ease, background-color 0.2s ease;
    background-color: transparent;
  }

  .dropzone.drag-over {
    border-color: var(--ptt-purple-100, #7c3aed);
    background-color: color-mix(in srgb, var(--ptt-purple-100, #7c3aed) 5%, transparent);
  }

  .dropzone.disabled {
    cursor: not-allowed;
    opacity: 0.6;
    border-color: var(--ptt-gray-20, #ccc);
    border-style: dashed;
  }

  .dropzone-label {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    width: 100%;
    text-align: center;
  }

  .dropzone-text {
    color: var(--ptt-gray-60, #666);
    font-size: 0.95rem;
  }

  .browse-link {
    color: var(--ptt-purple-100, #7c3aed);
    text-decoration: underline;
    font-weight: 500;
  }

  .file-input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
  }

  .disabled-message {
    color: var(--ptt-gray-60, #666);
    font-size: 0.9rem;
    text-align: center;
    margin: 0;
  }

  .error-message {
    color: var(--ptt-red-100, #dc2626);
    font-size: 0.85rem;
    margin-top: 0.5rem;
    text-align: center;
  }
</style>
